import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, addRecent } from "@/App";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

export default function SearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const resp = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`);
      setResults(resp.data);
      setOpen(true);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 350);
  };

  const selectResult = (r) => {
    addRecent({ id: r.id, symbol: r.symbol, name: r.name, type: r.type, chain: r.chain });
    const q = r.chain ? `?chain=${encodeURIComponent(r.chain)}` : "";
    navigate(`/asset/${r.type}/${encodeURIComponent(r.id)}${q}`);
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  return (
    <div ref={ref} className="relative" data-testid="search-bar">
      <div className="flex items-center bg-zinc-900/80 border border-zinc-800 rounded-sm px-2.5 py-1.5 gap-2">
        <MagnifyingGlass size={13} className="text-zinc-500 flex-shrink-0" />
        <input
          data-testid="search-input"
          type="text"
          value={query}
          onChange={onInput}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder="Search any asset..."
          className="bg-transparent text-xs text-zinc-200 placeholder-zinc-600 outline-none w-full font-data"
        />
        {query && (
          <button onClick={() => { setQuery(""); setResults([]); setOpen(false); }}>
            <X size={12} className="text-zinc-500 hover:text-zinc-300" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-sm shadow-xl z-50 max-h-[350px] overflow-y-auto" data-testid="search-results">
          {results.map((r, i) => (
            <button
              key={`${r.type}-${r.id}-${i}`}
              data-testid={`search-result-${i}`}
              onClick={() => selectResult(r)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-zinc-800 transition-colors border-b border-zinc-800/50 last:border-0"
            >
              {r.thumb ? (
                <img src={r.thumb} alt="" className="w-5 h-5 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                  <span className="text-[8px] text-zinc-400">{r.symbol?.charAt(0)}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-200 truncate">{r.name}</p>
                <p className="text-[10px] text-zinc-500">
                  {r.symbol} <span className="text-zinc-600">|</span> {r.type === "crypto" ? (r.source === "dexscreener" ? r.chain : "Crypto") : r.exchange || "Stock"}
                  {r.price_usd && <span className="text-zinc-400 ml-1">${parseFloat(r.price_usd).toLocaleString(undefined, {maximumFractionDigits: 6})}</span>}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {r.source === "dexscreener" && (
                  <span className="text-[8px] px-1 py-0.5 rounded-sm bg-violet-500/10 text-violet-400">DEX</span>
                )}
                <span className={`text-[9px] px-1.5 py-0.5 rounded-sm ${r.type === "crypto" ? "bg-emerald-500/10 text-emerald-400" : "bg-blue-500/10 text-blue-400"}`}>
                  {r.type === "crypto" ? "Crypto" : "Stock"}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && searching && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-sm shadow-xl z-50 p-4 text-center">
          <p className="text-xs text-zinc-500">Searching...</p>
        </div>
      )}
    </div>
  );
}
